import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import axios from "api/axios";
import { markSessionActivity, shouldSendSessionActivityHeader } from "utils/sessionActivity";

const IDLE_MS = 60 * 60 * 1000;
const CHECK_MS = 15 * 1000;
/** While the user is still active, refresh the JWT before the absolute 1h cookie expires. */
const SLIDE_PING_MS = 10 * 60 * 1000;
const ACTIVITY_EVENTS = ["mousedown", "keydown", "scroll", "touchstart", "click", "mousemove"];

function redirectIdleLogout() {
  try {
    sessionStorage.setItem("logout", "true");
  } catch {
    /* ignore */
  }
  window.location.replace("/login?error=session_expired");
}

/**
 * Sign out after 1 hour with no mouse/keyboard/touch activity.
 * While active, periodically hit /auth/me with X-Session-Activity so the JWT slides.
 */
const SessionIdleGuard = () => {
  const user = useSelector((state) => state.global.user);
  const lastActivityRef = useRef(Date.now());
  const lastSlidePingRef = useRef(0);

  useEffect(() => {
    if (!user?._id) return undefined;

    lastActivityRef.current = Date.now();
    markSessionActivity();

    const onActivity = () => {
      lastActivityRef.current = Date.now();
      markSessionActivity();
    };

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, onActivity, { passive: true })
    );

    const id = setInterval(() => {
      const idleFor = Date.now() - lastActivityRef.current;
      if (idleFor >= IDLE_MS) {
        clearInterval(id);
        axios.post("/auth/logout").catch(() => {}).finally(redirectIdleLogout);
        return;
      }

      // Keep the server session alive while the user is still interacting.
      if (
        shouldSendSessionActivityHeader() &&
        Date.now() - lastSlidePingRef.current >= SLIDE_PING_MS
      ) {
        lastSlidePingRef.current = Date.now();
        axios.get("/auth/me").catch(() => {});
      }
    }, CHECK_MS);

    return () => {
      clearInterval(id);
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, onActivity));
    };
  }, [user?._id]);

  return null;
};

export default SessionIdleGuard;
